import React, { useState, useEffect, useCallback } from 'react';
import { analyticsApi, factoriesApi, ordersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Scale, ArrowUpRight, ArrowDownRight, Plus, Wallet,
  ChevronDown, ChevronRight, Package, AlertTriangle, Trash2, Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

// ─── Factory Summary Card ───
const FactorySummaryCard = ({ summary }) => {
  const surplus = summary.total_surplus;
  const isPositive = surplus >= 0;
  return (
    <Card className="col-span-full" data-testid={`factory-summary-${summary.factory_id}`}>
      <CardContent className="p-4 md:p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center">
            <Scale className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="text-base font-bold text-foreground">{summary.factory_name}</h3>
            <p className="text-xs text-muted-foreground">{summary.dispatch_count} dispatch{summary.dispatch_count !== 1 ? 'es' : ''}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 rounded-lg bg-blue-50 dark:bg-blue-500/10">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Gold Sent</p>
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400 tabular-nums">{summary.total_gold_sent}g</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-slate-50 dark:bg-neutral-800">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Order Requirement</p>
            <p className="text-xl font-bold text-foreground tabular-nums">{summary.total_order_requirement}g</p>
          </div>
          <div className={cn(
            "text-center p-3 rounded-lg",
            isPositive ? "bg-emerald-50 dark:bg-emerald-500/10" : "bg-red-50 dark:bg-red-500/10"
          )}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
              {isPositive ? 'Surplus with Factory' : 'Deficit'}
            </p>
            <p className={cn(
              "text-xl font-bold tabular-nums",
              isPositive ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
            )}>
              {isPositive ? '+' : ''}{surplus}g
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Ledger Entry Row ───
const LedgerEntry = ({ entry, isSuperAdmin, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const date = new Date(entry.date);
  const dateStr = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const timeStr = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(entry.id);
      setConfirmOpen(false);
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div className="border-b border-border last:border-0" data-testid={`ledger-entry-${entry.id}`}>
      <div
        className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 px-3 md:px-4 py-3 hover:bg-muted/30 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center flex-shrink-0">
            <ArrowUpRight className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">{entry.gold_sent}g dispatched</span>
              <Badge variant="outline" className="text-[10px]">{entry.orders?.length || 0} orders</Badge>
            </div>
            <p className="text-xs text-muted-foreground">{dateStr} at {timeStr} &middot; by {entry.recorded_by}</p>
          </div>
          <div className="flex md:hidden items-center gap-2 flex-shrink-0">
            {isSuperAdmin && (
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmOpen(true); }}
                className="h-7 w-7 flex items-center justify-center rounded-md text-red-500 hover:bg-red-100 dark:hover:bg-red-500/15"
                data-testid={`delete-ledger-mobile-${entry.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
            {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
        <div className="flex items-center gap-3 md:gap-4 pl-11 md:pl-0">
          <div className="text-left md:text-right flex-shrink-0">
            <p className="text-[10px] text-muted-foreground">Requirement</p>
            <p className="text-sm font-semibold tabular-nums">{entry.total_order_requirement}g</p>
          </div>
          <div className={cn(
            "text-left md:text-right flex-shrink-0",
            entry.surplus >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
          )}>
            <p className="text-[10px] text-muted-foreground">Surplus</p>
            <p className="text-sm font-bold tabular-nums">{entry.surplus >= 0 ? '+' : ''}{entry.surplus}g</p>
          </div>
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            {isSuperAdmin && (
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmOpen(true); }}
                className="h-7 w-7 flex items-center justify-center rounded-md text-red-500 hover:bg-red-100 dark:hover:bg-red-500/15"
                data-testid={`delete-ledger-${entry.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
            {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-3 pl-14 space-y-1.5">
          {entry.notes && <p className="text-xs text-muted-foreground italic mb-2">{entry.notes}</p>}
          {(entry.orders || []).map((o, i) => (
            <div key={i} className="flex items-center gap-2 text-xs py-1 px-2 rounded bg-muted/30">
              <Package className="h-3 w-3 text-muted-foreground" />
              <span className="font-semibold">{o.co_number}</span>
              <span className="text-muted-foreground">{o.customer_name}</span>
              <span className="ml-auto font-bold tabular-nums">{o.gold_required}g</span>
              <span className="text-muted-foreground">({o.pieces} pcs)</span>
            </div>
          ))}
        </div>
      )}
      <Dialog open={confirmOpen} onOpenChange={(v) => { if (!deleting) setConfirmOpen(v); }}>
        <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Delete Gold Dispatch</DialogTitle>
            <DialogDescription>
              This will reverse <strong>{entry.gold_sent}g</strong> dispatched to <strong>{entry.factory_name}</strong> and undo all automations triggered for {entry.orders?.length || 0} order(s).
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-xs space-y-1">
            <p className="font-semibold text-red-700 dark:text-red-400">This action will:</p>
            <ul className="list-disc pl-4 text-red-600 dark:text-red-400 space-y-0.5">
              <li>Reverse gold issuance on each order</li>
              <li>Remove related movement & stage logs</li>
              <li>Remove triggered deadlines (if not yet completed)</li>
              <li>Delete this ledger entry permanently</li>
            </ul>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={deleting}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid={`confirm-delete-ledger-${entry.id}`}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete & Reverse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Issue Gold Dialog ───
const IssueGoldDialog = ({ open, onClose, factories, onSuccess }) => {
  const [factoryId, setFactoryId] = useState('');
  const [pendingOrders, setPendingOrders] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [allocations, setAllocations] = useState({}); // { order_id: grams }
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchPending = useCallback(async (fid) => {
    if (!fid) { setPendingOrders([]); return; }
    setLoading(true);
    try {
      const res = await analyticsApi.getPendingGoldOrders({ factory_id: fid });
      setPendingOrders(res.data);
    } catch { toast.error('Failed to load pending orders'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open && factoryId) fetchPending(factoryId);
    if (!open) {
      setFactoryId(''); setPendingOrders([]); setSelectedIds(new Set());
      setAllocations({}); setNotes('');
    }
  }, [open, factoryId, fetchPending]);

  const toggleOrder = (id, remaining) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setAllocations(a => { const n = { ...a }; delete n[id]; return n; });
      } else {
        next.add(id);
        setAllocations(a => ({ ...a, [id]: remaining }));
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === pendingOrders.length) {
      setSelectedIds(new Set());
      setAllocations({});
    } else {
      setSelectedIds(new Set(pendingOrders.map(o => o.order_id)));
      const allocs = {};
      pendingOrders.forEach(o => { allocs[o.order_id] = Number(o.gold_remaining_999 || 0); });
      setAllocations(allocs);
    }
  };

  const updateAllocation = (id, value) => {
    setAllocations(a => ({ ...a, [id]: parseFloat(value) || 0 }));
  };

  const selectedOrders = pendingOrders.filter(o => selectedIds.has(o.order_id));
  const totalAllocated = selectedOrders.reduce((s, o) => s + (allocations[o.order_id] || 0), 0);

  const handleSubmit = async () => {
    if (!factoryId || selectedIds.size === 0 || totalAllocated <= 0) return;
    const orderAllocations = selectedOrders.map(o => ({
      order_id: o.order_id,
      gold_amount: allocations[o.order_id] || 0,
    })).filter(a => a.gold_amount > 0);

    if (orderAllocations.length === 0) {
      toast.error('Enter gold amount for at least one order');
      return;
    }

    setSubmitting(true);
    try {
      const res = await analyticsApi.createGoldDispatch({
        factory_id: factoryId,
        order_allocations: orderAllocations,
        notes,
      });
      toast.success(res.data.message);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to dispatch gold');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading">Issue Gold to Factory</DialogTitle>
          <DialogDescription>Type the actual amount you're sending against each CO. The total at the bottom is what gets dispatched — no other input needed.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-auto">
          {/* Factory Selector */}
          <div className="space-y-1.5">
            <Label>Factory</Label>
            <Select value={factoryId} onValueChange={setFactoryId}>
              <SelectTrigger data-testid="dispatch-factory-select">
                <SelectValue placeholder="Select factory" />
              </SelectTrigger>
              <SelectContent>
                {factories.map(f => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pending Orders */}
          {factoryId && (
            <>
              <div className="flex items-center justify-between">
                <Label>Orders Pending Gold</Label>
                {pendingOrders.length > 0 && (
                  <button onClick={selectAll} className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline" data-testid="select-all-orders">
                    {selectedIds.size === pendingOrders.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>
              <div className="max-h-[320px] overflow-auto border rounded-lg">
                {loading ? (
                  <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
                ) : pendingOrders.length === 0 ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">No orders pending gold issuance</div>
                ) : (
                  pendingOrders.map(o => {
                    const isSelected = selectedIds.has(o.order_id);
                    const issuing = Number(allocations[o.order_id] || 0);
                    const remaining999 = Number(o.gold_remaining_999 || 0);
                    const remainingAfter = Math.max(0, Math.round((remaining999 - issuing) * 100) / 100);
                    const isPartial = isSelected && issuing > 0 && issuing < remaining999;
                    return (
                      <div
                        key={o.order_id}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0 transition-colors",
                          isSelected && "bg-blue-50 dark:bg-blue-500/10"
                        )}
                        data-testid={`pending-order-${o.co_number}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleOrder(o.order_id, o.gold_remaining_999)}
                        />
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleOrder(o.order_id, o.gold_remaining_999)}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold">{o.co_number}</span>
                            {o.gold_karat && <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">{o.gold_karat}</Badge>}
                            {o.priority === 'Urgent' && <Badge variant="destructive" className="text-[9px] px-1.5 py-0 h-4">URGENT</Badge>}
                            {isPartial && <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 text-amber-700 border-amber-300 bg-amber-50">PARTIAL · {remainingAfter.toFixed(2)}g still pending</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {o.customer_name} &middot; {o.pieces_to_factory} pcs &middot; needs <span className="font-semibold tabular-nums">{remaining999.toFixed(2)}g</span> <span className="text-[10px]">(999)</span>
                          </p>
                        </div>
                        {isSelected ? (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <Input
                              type="number" step="0.01" min="0" max={remaining999}
                              value={allocations[o.order_id] ?? ''}
                              onChange={e => updateAllocation(o.order_id, e.target.value)}
                              onClick={e => e.stopPropagation()}
                              className="w-24 h-9 text-sm font-bold text-right tabular-nums"
                              data-testid={`alloc-${o.co_number}`}
                            />
                            <span className="text-xs text-muted-foreground">g</span>
                          </div>
                        ) : (
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold tabular-nums">{remaining999.toFixed(2)}g</p>
                            <p className="text-[10px] text-muted-foreground">999 purity</p>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* Total + Notes */}
          {selectedIds.size > 0 && (
            <>
              <Separator />
              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/60 flex items-center justify-between gap-3" data-testid="dispatch-total">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total being dispatched</p>
                  <p className="text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                    {totalAllocated.toFixed(2)}<span className="text-base font-medium ml-1">g</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground">Sum of {selectedIds.size} {selectedIds.size === 1 ? 'CO' : 'COs'} · 999 purity</p>
                </div>
                <ArrowUpRight className="h-7 w-7 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              </div>

              <div className="space-y-1.5">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="e.g. Sending via courier"
                  value={notes} onChange={e => setNotes(e.target.value)}
                  rows={2} data-testid="dispatch-notes"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || selectedIds.size === 0 || totalAllocated <= 0}
            data-testid="confirm-gold-dispatch"
          >
            {submitting ? 'Dispatching...' : `Dispatch ${totalAllocated > 0 ? totalAllocated.toFixed(2) + 'g' : 'Gold'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Adjust Tally Section (Admin: correct historical wrong issuances) ───
const AdjustTallySection = ({ onChange }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ordersApi.getAll();
      const withGold = (res.data || []).filter(o => {
        const g = o.materials?.gold || {};
        return (g.required || 0) > 0;
      });
      setOrders(withGold);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const startEdit = (order) => {
    const g = order.materials?.gold || {};
    setEditingId(order.id);
    setEditValue(String(g.total_issued || 0));
    setEditReason('');
  };

  const cancelEdit = () => { setEditingId(null); setEditValue(''); setEditReason(''); };

  const saveEdit = async (orderId) => {
    const newTotal = parseFloat(editValue);
    if (isNaN(newTotal) || newTotal < 0) { toast.error('Invalid amount'); return; }
    setSaving(true);
    try {
      await analyticsApi.adjustGoldIssued(orderId, newTotal, editReason);
      toast.success('Tally corrected');
      cancelEdit();
      fetchAll();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const [search, setSearch] = useState('');
  const filtered = orders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (o.order_number || '').toLowerCase().includes(q) || (o.customer_name || '').toLowerCase().includes(q);
  });

  return (
    <Card data-testid="adjust-tally-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="font-heading text-base">Adjust Tally (Admin)</CardTitle>
              <p className="text-xs text-muted-foreground">Correct historical issued amounts. Required values are not changed — only what was actually issued.</p>
            </div>
          </div>
          <Input
            placeholder="Search CO or customer..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-9 w-full sm:w-56 text-sm"
            data-testid="adjust-tally-search"
          />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No orders with gold requirements found</div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto" data-testid="adjust-tally-scroll">
            {filtered.map(o => {
              const g = o.materials?.gold || {};
              const required = Number(g.required || 0);
              const issued = Number(g.total_issued || 0);
              const balance = Math.max(0, Math.round((required - issued) * 100) / 100);
              const isEditing = editingId === o.id;
              return (
                <div key={o.id} className="px-3 py-2.5 border-b border-border last:border-0" data-testid={`adjust-row-${o.order_number}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">{o.order_number}</span>
                        {g.karat && <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">{g.karat}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {o.customer_name} · req <span className="font-semibold tabular-nums">{required.toFixed(2)}g</span> · issued <span className={cn("font-semibold tabular-nums", issued > required && "text-red-600")}>{issued.toFixed(2)}g</span> · pending <span className="font-semibold tabular-nums">{balance.toFixed(2)}g</span>
                      </p>
                    </div>
                    {!isEditing ? (
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => startEdit(o)} data-testid={`adjust-btn-${o.order_number}`}>
                        Adjust
                      </Button>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Input
                          type="number" step="0.01" min="0" max={required}
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          className="w-24 h-8 text-sm font-bold text-right tabular-nums"
                          data-testid={`adjust-input-${o.order_number}`}
                        />
                        <span className="text-xs text-muted-foreground">g</span>
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <div className="mt-2 ml-0 flex items-center gap-2 flex-wrap">
                      <Input
                        placeholder="Reason (optional)"
                        value={editReason}
                        onChange={e => setEditReason(e.target.value)}
                        className="h-8 text-xs flex-1 min-w-[160px]"
                      />
                      <Button size="sm" className="h-8 text-xs" onClick={() => saveEdit(o.id)} disabled={saving} data-testid={`adjust-save-${o.order_number}`}>
                        Save
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={cancelEdit}>Cancel</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Main Page ───
const MetalLedger = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ entries: [], factory_summaries: [] });
  const [factories, setFactories] = useState([]);
  const [issueOpen, setIssueOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [ledgerRes, facListRes] = await Promise.all([
        analyticsApi.getMetalLedger(),
        factoriesApi.getAll(),
      ]);
      setData(ledgerRes.data);
      setFactories(Array.isArray(facListRes.data) ? facListRes.data : []);
    } catch {
      toast.error('Failed to load metal management');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDeleteDispatch = async (entryId) => {
    try {
      const res = await analyticsApi.deleteGoldDispatch(entryId);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete dispatch');
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" data-testid="metal-management-page">
        <Header title="Metal Management" />
        <div className="p-4 md:p-6 space-y-4">
          <Card><CardContent className="p-5"><Skeleton className="h-24 w-full" /></CardContent></Card>
          <Card><CardContent className="p-5"><Skeleton className="h-48 w-full" /></CardContent></Card>
        </div>
      </div>
    );
  }

  const hasSummaries = data.factory_summaries.length > 0;

  return (
    <div className="min-h-screen" data-testid="metal-management-page">
      <Header title="Metal Management" onRefresh={fetchData} />

      <div className="p-4 md:p-6 space-y-5">
        {/* Action Bar */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Track gold dispatches to factories and running balances
          </p>
          <Button onClick={() => setIssueOpen(true)} data-testid="issue-gold-btn">
            <Plus className="h-4 w-4 mr-2" />
            Issue Gold
          </Button>
        </div>

        {/* Factory Summaries */}
        {hasSummaries ? (
          <div className="grid grid-cols-1 gap-4">
            {data.factory_summaries.map(s => (
              <FactorySummaryCard key={s.factory_id} summary={s} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Wallet className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground mb-4">No gold dispatches recorded yet</p>
              <Button variant="outline" onClick={() => setIssueOpen(true)}>
                <Plus className="h-4 w-4 mr-2" /> Record First Dispatch
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Tally Adjustments — admin tool to fix historical wrong issuances (moved to bottom per request) */}

        {/* Transaction History */}
        {data.entries.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-neutral-800 flex items-center justify-center">
                  <Scale className="h-4 w-4 text-foreground" />
                </div>
                <div>
                  <CardTitle className="font-heading text-base">Dispatch History</CardTitle>
                  <p className="text-xs text-muted-foreground">{data.entries.length} record{data.entries.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="max-h-[500px]">
                {data.entries.map(e => (
                  <LedgerEntry key={e.id} entry={e} isSuperAdmin={isSuperAdmin} onDelete={handleDeleteDispatch} />
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Adjust Tally — admin tool, placed at the end of the page */}
        {isSuperAdmin && (
          <AdjustTallySection onChange={fetchData} />
        )}
      </div>

      {/* Issue Gold Dialog */}
      <IssueGoldDialog
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        factories={factories}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default MetalLedger;
